// Product-specific "next steps" copy shown on the thank-you screen and route.
// Post-submit checklist so borrowers
// know exactly what to expect after submitting.

import type { ProductKey } from "./pre-approval-products";

const UNIVERSAL_SUBHEAD =
  "Thank you — we've received your information. We'll review your submission and follow up with next steps to complete your application.";

export interface ThankYouCopy {
  headline: string;
  subhead: string;
  timeline: string;
  nextSteps: string[];
  documents: string[];
}

const commonDocs = [
  "Government-issued photo ID for every borrower / guarantor",
  "Two months of most recent bank / brokerage statements (liquid reserves)",
  "Entity docs if closing in an LLC / Corp / LP (Articles, EIN letter, Operating Agreement)",
  "Voided check or wire instructions for closing",
];

const commonSteps = [
  "A licensed loan officer will call or email you within one business day.",
  "You'll receive an initial scenario review once we have your credit authorization and required documents — formal pricing, LTV, points, and reserves are issued after underwriting.",
  "We'll send a secure document upload link so we can begin the credit and property review.",
];


export const THANK_YOU_COPY: Record<ProductKey, ThankYouCopy> = {
  dscr: {
    headline: "Your DSCR file is in.",
    subhead: UNIVERSAL_SUBHEAD,
    timeline: "Initial scenario review: same day / next business day after credit auth + docs. Close: 21–30 days once appraisal, title, and full underwriting are complete.",
    nextSteps: [
      ...commonSteps,
      "We'll order an appraisal with a 1007 market rent schedule so DSCR can be confirmed.",
      "Title, insurance quote, and property inspection get scheduled in parallel.",
    ],
    documents: [
      ...commonDocs,
      "Current lease(s) or expected market rent (1007 will be ordered)",
      "Property tax + insurance quote (or last bill)",
      "HOA statement if applicable",
    ],
  },
  multifamily: {
    headline: "Your multifamily file is in.",
    subhead: UNIVERSAL_SUBHEAD,
    timeline: "Indicative term sheet: 1–2 business days after we receive the rent roll and T-12. Close: 45–60 days, driven by the commercial appraisal and property condition report.",
    nextSteps: [
      ...commonSteps,
      "We'll underwrite your rent roll and T-12 to a net operating income figure and size the loan to a 1.20x DSCR.",
      "You'll receive an indicative term sheet with loan amount, rate range, and the cap rate we used.",
      "Commercial appraisal, property condition report, and (where required) Phase I environmental get ordered on term sheet acceptance.",
      "Entity or single-purpose entity documents are reviewed in parallel with title and insurance.",
    ],
    documents: [
      ...commonDocs,
      "Certified current rent roll with lease dates and concessions",
      "Trailing 12-month operating statement (T-12) plus last two annual P&Ls",
      "Most recent property tax bill and insurance declarations page",
      "Sponsor personal financial statement (net worth and liquidity)",
      "Capital improvement history and any planned value-add budget",
      "Purchase contract, or existing note and payoff statement on a refinance",
    ],
  },
  hardmoney: {
    headline: "Your private money request is in.",
    subhead: UNIVERSAL_SUBHEAD,
    timeline: "Indicative terms: same day on complete scenarios. Fund: 5–14 days, with title and insurance usually setting the pace.",
    nextSteps: [
      ...commonSteps,
      "We'll order a BPO (48–72 hours) or a rush appraisal where the deal requires one.",
      "Title is opened immediately and your exit — sale or refinance — is stress-tested against the term.",
      "If renovation dollars are involved, we'll build a draw schedule held in escrow.",
      "We'll map the takeout now so the refinance into a DSCR, portfolio, or multifamily loan is already lined up.",
    ],
    documents: [
      ...commonDocs,
      "Purchase contract, auction confirmation, or existing note and payoff statement",
      "Written exit plan with dates (sale, refinance, or takeout loan)",
      "Scope of work and renovation budget, if funds are being escrowed",
      "Photos or an inspection report showing current property condition",
      "Track record — addresses and outcomes of deals completed in the last 36 months",
    ],
  },
  heloc: {
    headline: "Your investment property HELOC request is in.",
    subhead: UNIVERSAL_SUBHEAD,
    timeline: "Initial scenario review: same day after credit auth + docs. Close: typically 3–5 weeks with appraisal and title setting the pace.",
    nextSteps: [
      ...commonSteps,
      "We'll confirm unit count, leases, and gross rents to size the line against a 1.00 DSCR.",
      "We'll map your draw plan — how much you want at close and how much you want to keep available.",
      "Appraisal and title are ordered once the scenario and line amount are confirmed.",
    ],
    documents: [
      ...commonDocs,
      "Current rent roll and all executed leases",
      "Most recent property tax bill and insurance declarations page",
      "Existing note and payoff statement, if refinancing",
      "Schedule of real estate owned showing your investing track record",
    ],
  },
  bridge: {
    headline: "Your bridge scenario is in.",
    subhead: UNIVERSAL_SUBHEAD,
    timeline: "Initial scenario review: same day after credit auth + docs. Close: 7–14 days is normal for bridge on complete files.",
    nextSteps: [
      ...commonSteps,
      "We'll order a rush appraisal or accept a recent BPO where possible.",
      "Title is opened immediately and the exit strategy is stress-tested.",
    ],
    documents: [
      ...commonDocs,
      "Current mortgage payoff / HELOC statement",
      "Signed purchase contract (if a purchase)",
      "Exit strategy documentation (refi pre-approval or listing agreement)",
    ],
  },
  flip: {
    headline: "Your fix-and-flip file is in.",
    subhead: UNIVERSAL_SUBHEAD,
    timeline: "Initial scenario review: same day after credit auth + docs. Close: 10–21 days once appraisal and scope of work are complete.",
    nextSteps: [
      ...commonSteps,
      "We'll order an ARV appraisal with a scope-of-work review.",
      "Draw schedule will be tailored to your rehab budget and timeline.",
      "Title, insurance (builder's risk), and contractor W-9 collected in parallel.",
    ],
    documents: [
      ...commonDocs,
      "Signed purchase contract",
      "Itemized scope of work / rehab budget",
      "Contractor bids and license (if using a GC)",
      "Track record of prior flips (HUD-1 / closing statements)",
    ],
  },
  construction: {
    headline: "Your ground-up construction file is in.",
    subhead: UNIVERSAL_SUBHEAD,
    timeline: "Pricing feedback: 1–2 business days. Close: 30–45 days depending on plans, permits, and builder review.",
    nextSteps: [
      ...commonSteps,
      "We'll review the land cost, hard cost budget, and permitting status.",
      "Builder is vetted (license, insurance, track record).",
      "Draw schedule and interest reserve are structured with your GC.",
    ],
    documents: [
      ...commonDocs,
      "Land purchase contract or current title (if owned)",
      "Approved plans / permits (or status)",
      "Builder contract, license, and insurance",
      "Itemized hard cost budget",
    ],
  },
  portfolio: {
    headline: "Your portfolio loan file is in.",
    subhead: UNIVERSAL_SUBHEAD,
    timeline: "Pricing feedback: 1–2 business days. Close: 30–45 days depending on door count and title complexity.",
    nextSteps: [
      ...commonSteps,
      "We'll build the blended DSCR across every property in the pool.",
      "Appraisals ordered in bulk to compress timeline.",
      "Title runs on every asset; existing mortgages are payoff-lined.",
    ],
    documents: [
      ...commonDocs,
      "Full rent roll (address, rent, lease dates, tenant name)",
      "Current mortgage statements on every property",
      "Property tax + insurance on every property",
      "T-12 operating statements if available",
    ],
  },
  section8: {
    headline: "Your Section 8 / HAP file is in.",
    subhead: UNIVERSAL_SUBHEAD,
    timeline: "Initial scenario review: same day after credit auth + docs. Close: 21–30 days once HAP + HQS docs are collected.",
    nextSteps: [
      ...commonSteps,
      "We'll verify the HAP contract and current HAP payment schedule.",
      "HQS inspection results confirmed; appraisal ordered with market rent.",
    ],
    documents: [
      ...commonDocs,
      "Current HAP contract (all pages)",
      "Most recent HAP payment schedule / voucher letter",
      "HQS inspection results (pass letter)",
      "Tenant portion lease (if any tenant-paid rent)",
    ],
  },
  str: {
    headline: "Your short-term rental (STR) file is in.",
    subhead: UNIVERSAL_SUBHEAD,
    timeline: "Initial scenario review: same day after credit auth + docs. Close: 21–30 days on complete files.",
    nextSteps: [
      ...commonSteps,
      "We'll pull an AirDNA report or verify your trailing 12-month host statements.",
      "Jurisdiction / permitting verified so the loan doesn't get called.",
      "Appraisal ordered with STR income addendum where allowed.",
    ],
    documents: [
      ...commonDocs,
      "Airbnb / VRBO trailing 12-month host statements",
      "STR permit or business license (where required)",
      "HOA or condo association STR rules (if applicable)",
    ],
  },
  foreign: {
    headline: "Your foreign national investor file is in.",
    subhead: UNIVERSAL_SUBHEAD,
    timeline: "Pricing feedback: 1–2 business days. Close: 30–45 days depending on document delivery from abroad.",
    nextSteps: [
      ...commonSteps,
      "We'll verify your passport, visa / residency status, and country of tax residence.",
      "Assets seasoned in a US bank (or transferred at close) will be confirmed.",
      "Appraisal + title ordered immediately in your entity's name.",
    ],
    documents: [
      "Valid passport (photo page)",
      "US visa or residency permit (if any)",
      "Home country tax return or accountant letter (2 years)",
      "2 months of bank statements from home country AND US (if seasoned)",
      "Reference letter from your home bank",
      "Entity docs if closing in a US LLC (Articles, EIN, Operating Agreement)",
    ],
  },
};

export function copyForProduct(product: string): ThankYouCopy {
  return (THANK_YOU_COPY as Record<string, ThankYouCopy>)[product] ?? THANK_YOU_COPY.dscr;
}
